import React, { useState } from 'react';

/**
 * Model-profile switcher + (optional) management actions.
 *
 * The active profile is the source of truth for which models a run uses;
 * selecting a different profile switches it. With `manage`, also exposes
 * New / Duplicate / Rename / Delete. New and Duplicate activate the created
 * profile so it can be edited immediately. Delete is disabled when only one
 * profile remains (the backend also rejects it). Management actions live only
 * in the Model Defaults tab; the left panel renders this select-only (no
 * `manage`).
 *
 * @param {object}   props
 * @param {Array}    props.profiles          - [{profile_name, is_active}].
 * @param {string}   props.activeProfileName - Currently active profile.
 * @param {Function} props.onSwitch          - async (name) => switch active.
 * @param {Function} props.onCreate          - async (name, {duplicateFrom, activate}).
 * @param {Function} props.onRename          - async (oldName, newName).
 * @param {Function} props.onDelete          - async (name).
 * @param {boolean}  props.manage            - Show management actions.
 * @param {boolean}  props.isDirty           - Unsaved edits a reload discards.
 * @param {object}   props.style             - Optional wrapper style overrides.
 */
function ProfileSwitcher({
  profiles = [],
  activeProfileName = 'Default',
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  manage = false,
  isDirty = false,
  style = {},
}) {
  const [mode, setMode] = useState('idle'); // idle | create | rename | duplicate
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Bumped to remount the <select> so it snaps back to the active profile
  // after a switch is cancelled at the confirm prompt (a no-op state change
  // would not reliably reset the native control's transient selection).
  const [resync, setResync] = useState(0);

  // Alphanumeric, case-insensitive, numeric-aware ("Work 2" before
  // "Work 10") — the server list is creation-ordered.
  const names = (profiles.length
    ? profiles
    : [{ profile_name: activeProfileName, is_active: true }]
  ).slice().sort((a, b) => String(a.profile_name).localeCompare(
    String(b.profile_name), undefined, { numeric: true, sensitivity: 'base' },
  ));

  // Warn before an action that reloads model settings and would silently
  // discard unsaved edits. Returns false if the user cancels.
  const confirmDiscard = () => {
    if (!isDirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(
      'You have unsaved model changes that switching profiles will '
        + 'discard. Continue?',
    );
  };

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setMode('idle');
      setName('');
    } catch (e) {
      setError(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const start = (m) => {
    setError(null);
    setName(
      m === 'rename'
        ? activeProfileName
        : m === 'duplicate'
          ? `${activeProfileName} copy`
          : '',
    );
    setMode(m);
  };

  const submit = () => {
    const n = name.trim();
    if (!n) {
      setError('Name required');
      return;
    }
    // Rename keeps the active profile/data, so it never discards edits.
    if (mode === 'rename') {
      run(() => onRename(activeProfileName, n));
      return;
    }
    // Create and Duplicate both activate the new profile, reloading (and
    // discarding) the current selection — confirm first.
    if (!confirmDiscard()) return;
    if (mode === 'create') run(() => onCreate(n, { activate: true }));
    else if (mode === 'duplicate')
      run(() => onCreate(n, { duplicateFrom: activeProfileName, activate: true }));
  };

  const btn = {
    fontSize: '0.78rem',
    padding: '3px 8px',
    borderRadius: 4,
    border: '1px solid var(--input-border, #ccc)',
    background: 'var(--hover-color, transparent)',
    color: 'var(--text-secondary, #555)',
    cursor: 'pointer',
  };

  // Idle management actions, rendered as text labels (with hover tooltips).
  const manageActions = [
    {
      key: 'create', tip: 'New profile', label: '+ New',
      onClick: () => start('create'),
    },
    {
      key: 'duplicate', tip: 'Duplicate profile', label: 'Duplicate',
      onClick: () => start('duplicate'),
    },
    {
      key: 'rename', tip: 'Rename profile', label: 'Rename',
      onClick: () => start('rename'),
    },
    {
      key: 'delete', tip: 'Delete profile', label: 'Delete',
      danger: true, disabled: names.length <= 1,
      onClick: () => {
        // Deleting the active profile reassigns + reloads → discards.
        if (!confirmDiscard()) return;
        run(() => onDelete(activeProfileName));
      },
    },
  ];

  return (
    <div style={{ marginBottom: 10, ...style }}>
      <label
        style={{
          display: 'block',
          fontSize: '0.78rem',
          color: 'var(--text-secondary, #888)',
          marginBottom: 3,
        }}
      >
        Profile
      </label>
      <select
        key={`profile-select-${activeProfileName}-${resync}`}
        value={activeProfileName}
        disabled={busy}
        onChange={(e) => {
          const next = e.target.value;
          if (next === activeProfileName) return;
          if (!confirmDiscard()) {
            // Cancelled: snap the controlled select back to the active value.
            setResync((r) => r + 1);
            return;
          }
          run(() => onSwitch(next));
        }}
        style={{ width: '100%', fontSize: '0.9em' }}
        aria-label="Active model profile"
      >
        {names.map((p) => (
          <option key={p.profile_name} value={p.profile_name}>
            {p.profile_name}
          </option>
        ))}
      </select>

      {manage && mode === 'idle' && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {manageActions.map(
            ({ key, tip, label, danger, disabled, onClick }) => (
              <button
                key={key}
                type="button"
                title={tip}
                aria-label={tip}
                disabled={!!disabled || busy}
                onClick={onClick}
                style={{
                  ...btn,
                  ...(danger
                    ? {
                        color: disabled
                          ? 'var(--text-secondary, #aaa)'
                          : '#e5393f',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }
                    : {}),
                }}
              >
                {label}
              </button>
            ),
          )}
        </div>
      )}

      {manage && mode !== 'idle' && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              mode === 'rename' ? 'New name' : 'New profile name'
            }
            style={{ flex: 1, minWidth: 120, fontSize: '0.85em' }}
            aria-label="Profile name"
          />
          <button type="button" style={btn} disabled={busy} onClick={submit}>
            {mode === 'rename' ? 'Rename' : mode === 'duplicate' ? 'Duplicate' : 'Create'}
          </button>
          <button
            type="button"
            style={btn}
            disabled={busy}
            onClick={() => {
              setMode('idle');
              setError(null);
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{ color: '#e5393f', fontSize: '0.75rem', marginTop: 4 }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default ProfileSwitcher;
